import { faTimes } from '@fortawesome/pro-regular-svg-icons'
import { ChangeEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IWBadgeGroup from 'shared/components/thunderbolt/IWBadgeGroup'
import IWButton from 'shared/components/thunderbolt/IWButton'
import IWFormSection from 'shared/components/thunderbolt/IWFormSectionContainer'
import IWGeneralBadge from 'shared/components/thunderbolt/IWGeneralBadge'
import { IWFileInput } from 'shared/components/thunderbolt/IWTextInput'
import styled from 'styled-components'

const StyledActionableSection = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 1rem;
  align-items: flex-end;
`

const StyledBadge = styled(IWGeneralBadge)`
  text-transform: none;
`

const FileNameBadge = ({
  filename,
  onIconClick,
}: {
  filename: string | undefined
  onIconClick: () => void
}) => {
  const { t } = useTranslation()
  const title = t('loadScheduling.uploadedFilenameLabel')

  return (
    <IWBadgeGroup>
      {filename ? (
        <StyledBadge
          data-testid="ls-filename-badge"
          label={`${title}: ${filename || 'No file selected'}`}
          color="primary"
          icon={faTimes}
          iconPosition="trailing"
          onIconClick={onIconClick}
        />
      ) : (
        <StyledBadge
          data-testid="ls-filename-badge"
          label={`${title}: ${filename || 'No file selected'}`}
          color="primary"
        />
      )}
    </IWBadgeGroup>
  )
}

const FileUploadSection = ({
  onChange,
  onClear,
}: {
  onChange: (filename: string, file: File) => void
  onClear: () => void
}) => {
  const { t } = useTranslation()
  const filesRef = useRef<HTMLInputElement | null>(null)
  const [selectedFilename, setSelectedFilename] = useState<string | undefined>(
    undefined,
  )

  const clearInputFiles = (): void => {
    if (filesRef?.current) {
      filesRef.current.value = ''
    }

    setSelectedFilename(undefined)
    onClear()
  }

  const onFileSelect = (event: ChangeEvent<HTMLInputElement>): void => {
    try {
      const files = event && Array.from(event.target.files || [])

      if (!files || files.length === 0) {
        clearInputFiles()
        return
      }

      if (files.length === 0) {
        return
      }

      const [file] = files
      const { name: filename } = file

      if (!filename.endsWith('.csv')) {
        throw new Error('Unsupported file type')
      }

      setSelectedFilename(filename)

      onChange(filename, file)
    } catch (err) {
      console.error('Error uploading file', err)
      onClear()
    }
  }

  return (
    <div style={{ marginBottom: '0rem' }}>
      <IWFormSection
        sectionTitle={t('loadScheduling.sections.uploadTitle')}
        sectionDescription={t('loadScheduling.sections.uploadDescription')}
      >
        <FileNameBadge
          filename={selectedFilename}
          onIconClick={clearInputFiles}
        />

        <StyledActionableSection>
          <div style={{ display: 'none' }}>
            <IWFileInput
              ref={filesRef}
              type="file"
              name="file"
              accept=".csv"
              onChange={onFileSelect}
            />
          </div>

          <IWButton variant="outline" onClick={() => filesRef.current?.click()}>
            {t('button.upload')}
          </IWButton>
        </StyledActionableSection>
      </IWFormSection>
    </div>
  )
}

export default FileUploadSection
